import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Eye, KeyRound, Lock, PencilLine, Power, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { useAuth } from "../../auth/context";
import { useAppServices } from "../../hook/services";
import { copyText, cx, formatDate, initials } from "../../utils";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import Input, { Label, Select } from "../../components/ui/Input";
import Modal from "../../components/ui/Modal";
import Skeleton from "../../components/ui/Skeleton";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import { ago, Pill } from "../../components/directory/shared";

/**
 * Who can sign in to the super admin, and what they can do. Admins only.
 *
 * There is no email sending, so adding a member (or resetting a password)
 * shows a temporary password once, to pass on. The member sets their own at
 * first sign-in. The backend refuses anything that would lock the team out —
 * changing your own role or access, or losing the last admin.
 */

const ROLES = [
  { value: "admin", label: "Admin", icon: ShieldCheck, text: "Everything, including managing the team." },
  { value: "member", label: "Member", icon: PencilLine, text: "Everything except managing the team." },
  { value: "viewer", label: "Viewer", icon: Eye, text: "Can see every page, but can't change anything." },
];
const ROLE = Object.fromEntries(ROLES.map((r) => [r.value, r]));

/* --------------------------------------------- temporary password, shown once */

const PasswordReveal = ({ title, who, password, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await copyText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal
      title={title}
      description={`Send ${who} this temporary password. It's shown only once — they'll choose their own when they sign in.`}
      icon={<KeyRound size={18} />}
      onClose={onClose}
      footer={
        <Button variant="gradient" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="flex items-center gap-2 rounded-xl border border-ink-200 bg-ink-50 px-3.5 py-3">
        <code className="min-w-0 flex-1 select-all break-all font-mono text-[15px] font-semibold tracking-wide text-ink-900">{password}</code>
        <Button size="sm" variant="secondary" onClick={copy} icon={copied ? <Check size={13} /> : <Copy size={13} />}>
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <p className="mt-3 text-[12.5px] text-ink-500">
        Sign-in page: <span className="font-mono text-ink-700">{window.location.origin}/login</span>
      </p>
    </Modal>
  );
};

/* --------------------------------------------------------------- add member */

const AddMember = ({ onClose, onAdded }) => {
  const { team } = useAppServices();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const { response, error: err } = await team.Create({ name: name.trim(), email: email.trim(), role });
    setSaving(false);
    if (err || !response) return setError(err?.message || err?.error || "Could not add this member.");
    onAdded(response);
  };

  return (
    <Modal
      title="Add a team member"
      description="They'll get a temporary password to sign in with."
      icon={<UserPlus size={18} />}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="add-member" variant="gradient" loading={saving} disabled={saving}>
            Add member
          </Button>
        </>
      }
    >
      <form id="add-member" onSubmit={submit} className="space-y-3.5">
        <div className="space-y-1.5">
          <Label htmlFor="m-name" required>
            Name
          </Label>
          <Input id="m-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="m-email" required>
            Email
          </Label>
          <Input id="m-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-ink-800">Role</legend>
          <div className="space-y-2 pt-1">
            {ROLES.map((r) => (
              <label
                key={r.value}
                className={cx(
                  "flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-2.5 transition-colors",
                  role === r.value ? "border-brand-300 bg-brand-50/60" : "border-ink-200 hover:border-ink-300",
                )}
              >
                <input type="radio" name="role" value={r.value} checked={role === r.value} onChange={() => setRole(r.value)} className="mt-1 accent-brand-600" />
                <span>
                  <span className="flex items-center gap-1.5 text-[13px] font-medium text-ink-900">
                    <r.icon size={13} className="text-ink-500" aria-hidden />
                    {r.label}
                  </span>
                  <span className="block text-[12px] text-ink-500">{r.text}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        {error && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error}</p>}
      </form>
    </Modal>
  );
};

/* --------------------------------------------------------------------- page */

const Team = () => {
  const { team } = useAppServices();
  const { isAdmin } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const { response, error } = await team.List();
    if (error) toast(error?.message || error?.error || "Could not load the team", "error");
    setData(response || { members: [], counts: { total: 0, admins: 0, disabled: 0 } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <Card>
        <EmptyState icon={<Lock size={24} />} title="Admins only" description="Only admins can manage the team. Ask an admin if you need a different role." />
      </Card>
    );
  }

  const run = async (member, fn, success) => {
    setBusy(member._id);
    const { response, error } = await fn();
    setBusy(null);
    if (error) {
      toast(error?.message || error?.error || "That didn't work", "error");
      return null;
    }
    if (success) toast(success, "success");
    await load();
    return response;
  };

  const changeRole = (m, role) => run(m, () => team.Update(m._id, { role }), `${m.name} is now ${ROLE[role].label.toLowerCase()}`);

  const toggleAccess = async (m) => {
    if (!m.disabled) {
      const ok = await confirm({
        title: `Turn off ${m.name}'s access?`,
        message: "They're signed out at once and can't sign in until you turn it back on.",
        confirmLabel: "Turn off access",
      });
      if (!ok) return;
    }
    run(m, () => team.Update(m._id, { disabled: !m.disabled }), m.disabled ? `${m.name} can sign in again` : `${m.name}'s access is off`);
  };

  const resetPassword = async (m) => {
    const ok = await confirm({
      title: `Reset ${m.name}'s password?`,
      message: "Their current password stops working and they're signed out. You'll get a temporary password to send them.",
      confirmLabel: "Reset password",
      tone: "brand",
    });
    if (!ok) return;
    const response = await run(m, () => team.ResetPassword(m._id));
    if (response?.temporaryPassword) setReveal({ title: "Password reset", who: m.name, password: response.temporaryPassword });
  };

  const remove = async (m) => {
    const ok = await confirm({
      title: `Remove ${m.name} from the team?`,
      message: "They're signed out at once and can't sign in again. You can add the same email later.",
      confirmLabel: "Remove",
    });
    if (!ok) return;
    run(m, () => team.Remove(m._id), `${m.name} was removed`);
  };

  const added = (response) => {
    setAdding(false);
    load();
    setReveal({ title: "Member added", who: response.member?.name || "them", password: response.temporaryPassword });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-ink-900">Team</h2>
          <p className="text-[13px] text-ink-500">
            {data ? `${data.counts.total} ${data.counts.total === 1 ? "person" : "people"} · ${data.counts.admins} ${data.counts.admins === 1 ? "admin" : "admins"}${data.counts.disabled ? ` · ${data.counts.disabled} turned off` : ""}` : "Who can sign in to this admin, and what they can do."}
          </p>
        </div>
        <Button variant="gradient" icon={<UserPlus size={14} />} onClick={() => setAdding(true)}>
          Add member
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {ROLES.map((r) => (
          <div key={r.value} className="rounded-xl border border-ink-200/70 bg-white px-4 py-3">
            <p className="flex items-center gap-1.5 text-[13px] font-semibold text-ink-900">
              <r.icon size={14} className="text-brand-600" aria-hidden />
              {r.label}
            </p>
            <p className="mt-0.5 text-[12.5px] text-ink-500">{r.text}</p>
          </div>
        ))}
      </div>

      <Card padded={false}>
        {!data ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2].map((k) => (
              <div key={k} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-9 w-9" rounded="rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[54rem] text-sm">
              <thead>
                <tr className="border-b border-ink-150 text-left">
                  {["Member", "Role", "Status", "Last sign-in", "Added", ""].map((h, i) => (
                    <th key={i} className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-150">
                {data.members.map((m) => (
                  <tr key={m._id} className={cx("transition-opacity", m.disabled && "opacity-60", busy === m._id && "opacity-50")}>
                    <td className="max-w-[18rem] px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-gradient text-[11px] font-bold text-white">{initials(m.name || m.email)}</span>
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 truncate font-medium text-ink-900">
                            {m.name || "—"}
                            {m.isYou && <Pill tone="brand">You</Pill>}
                          </p>
                          <p className="truncate text-[12px] text-ink-500">{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {m.isYou ? (
                        <span className="text-[13px] text-ink-700" title="You can't change your own role">
                          {ROLE[m.role]?.label || m.role}
                        </span>
                      ) : (
                        <Select aria-label={`Role for ${m.name}`} value={m.role || ""} onChange={(e) => changeRole(m, e.target.value)} disabled={busy === m._id} className="!h-8 w-32 !text-[13px]">
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </Select>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col items-start gap-1">
                        {m.disabled ? <Pill tone="rose">Access off</Pill> : <Pill tone="emerald">Active</Pill>}
                        {!m.disabled && m.mustChangePassword && <Pill tone="amber">{m.lastLoginAt ? "New password due" : "Hasn't signed in"}</Pill>}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-ink-500">{m.lastLoginAt ? ago(m.lastLoginAt) : <span className="text-ink-300">Never</span>}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-ink-500">
                      {formatDate(m.createdAt)}
                      {m.invitedBy && <span className="block text-[11.5px] text-ink-400">by {m.invitedBy}</span>}
                    </td>
                    <td className="px-5 py-3">
                      {!m.isYou && (
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => resetPassword(m)} disabled={busy === m._id} title="Reset password" aria-label={`Reset ${m.name}'s password`} className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700">
                            <KeyRound size={15} />
                          </button>
                          <button type="button" onClick={() => toggleAccess(m)} disabled={busy === m._id} title={m.disabled ? "Turn access back on" : "Turn off access"} aria-label={m.disabled ? `Turn ${m.name}'s access back on` : `Turn off ${m.name}'s access`} className={cx("grid h-8 w-8 place-items-center rounded-lg hover:bg-ink-100", m.disabled ? "text-emerald-600" : "text-ink-400 hover:text-amber-600")}>
                            <Power size={15} />
                          </button>
                          <button type="button" onClick={() => remove(m)} disabled={busy === m._id} title="Remove from team" aria-label={`Remove ${m.name}`} className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-[12px] text-ink-400">Change your own password from your name at the bottom of the sidebar.</p>

      {adding && <AddMember onClose={() => setAdding(false)} onAdded={added} />}
      {reveal && <PasswordReveal {...reveal} onClose={() => setReveal(null)} />}
    </div>
  );
};

export default Team;
